import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import AllergyManagement from '../AllergyManagement';
import PaymentList from '../PaymentList';
import PatientPhotoGallery from '../PatientPhotoGallery';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate } from '../utils/dateFormat';
import { BolivianDateInput } from '../components/common/BolivianDateInput';

interface TreatmentStep {
  id: number;
  stepNumber: number;
  name: string;
  description?: string;
  cost: number;
  completed: boolean;
  completedAt?: string;
}

interface Treatment {
  id: number;
  patientId: number;
  name: string;
  status: string;
  totalCost: number;
  totalPaid: number;
  totalDue: number;
  isCompleted: boolean;
  isMultiStep: boolean;
  steps?: TreatmentStep[];
}

interface PatientSummary {
  id: number;
  presentedName?: string;
  firstName?: string;
  firstLastName?: string;
  phoneNumber?: string;
  email?: string;
  profilePicturePath?: string;
  hasCriticalAllergy?: boolean;
  balanceDue?: number;
}

interface MedicalStory {
  id: number;
  date: string;
  description: string;
  appointmentId?: number | null;
}

type TabId = 'overview' | 'ehr' | 'allergies' | 'billing' | 'photos';

const panelStyle: React.CSSProperties = {
  borderRadius: 10,
  padding: 16
};

const cardStyle: React.CSSProperties = {
  borderRadius: 8,
  padding: 12
};

export default function TreatmentWorkspacePage({
  token,
  treatmentId,
  patientId,
  appointmentId,
  onBack,
  onOpenPatientProfile
}: {
  token: string;
  treatmentId: number;
  patientId?: number | null;
  appointmentId?: number | null;
  onBack: () => void;
  onOpenPatientProfile: (id: number) => void;
}) {
  const theme = useThemedStyles();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [stories, setStories] = useState<MedicalStory[]>([]);
  const [paymentTotal, setPaymentTotal] = useState(0);

  const [ehrDate, setEhrDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [ehrFreeText, setEhrFreeText] = useState('');
  const [ehrChiefComplaint, setEhrChiefComplaint] = useState('');
  const [ehrExam, setEhrExam] = useState('');
  const [ehrDiagnosis, setEhrDiagnosis] = useState('');
  const [ehrPlan, setEhrPlan] = useState('');
  const [ehrSaving, setEhrSaving] = useState(false);

  const selectedTreatment = useMemo(
    () => treatments.find((item) => item.id === treatmentId) || null,
    [treatments, treatmentId]
  );

  const fetchCoreData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let resolvedPatientId = typeof patientId === 'number' ? patientId : null;

      if (!resolvedPatientId) {
        const treatmentRes = await fetch(getApiUrl(`/treatments/${treatmentId}`), {
          headers: getAuthHeaders(token)
        });
        if (!treatmentRes.ok) {
          throw new Error('Failed to load treatment details');
        }
        const treatmentData = await treatmentRes.json();
        resolvedPatientId = typeof treatmentData?.patientId === 'number' ? treatmentData.patientId : null;
      }

      if (!resolvedPatientId) {
        throw new Error('Treatment has no linked patient');
      }

      const [patientRes, treatmentsRes, storiesRes, paymentsRes] = await Promise.all([
        fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.PATIENTS}/${resolvedPatientId}`), { headers: getAuthHeaders(token) }),
        fetch(getApiUrl(`/patients/${resolvedPatientId}/treatments`), { headers: getAuthHeaders(token) }),
        fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_MEDICAL_STORIES, { patientId: resolvedPatientId }), { headers: getAuthHeaders(token) }),
        fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PAYMENTS, { patientId: resolvedPatientId }), { headers: getAuthHeaders(token) })
      ]);

      if (!patientRes.ok || !treatmentsRes.ok || !storiesRes.ok || !paymentsRes.ok) {
        throw new Error('Failed to load workspace data');
      }

      const [patientData, treatmentsData, storiesData, paymentsData] = await Promise.all([
        patientRes.json(),
        treatmentsRes.json(),
        storiesRes.json(),
        paymentsRes.json()
      ]);

      setPatient(patientData);
      setTreatments(Array.isArray(treatmentsData) ? treatmentsData : []);
      setStories(Array.isArray(storiesData) ? storiesData : []);
      const total = Array.isArray(paymentsData)
        ? paymentsData.reduce((sum: number, payment: any) => sum + (typeof payment?.amount === 'number' ? payment.amount : 0), 0)
        : 0;
      setPaymentTotal(total);
    } catch (e: any) {
      setError(e?.message || 'Failed to load treatment workspace');
    } finally {
      setLoading(false);
    }
  }, [patientId, token, treatmentId]);

  useEffect(() => {
    fetchCoreData();
  }, [fetchCoreData]);

  const changeTreatmentStatus = async (status: string) => {
    if (!selectedTreatment) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(`/treatments/${selectedTreatment.id}/status`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Failed to update treatment status');
      fetchCoreData();
    } catch (e: any) {
      setError(e?.message || 'Failed to update treatment status');
    }
  };

  const updateStepCompletion = async (stepId: number, completed: boolean) => {
    if (!selectedTreatment) return;
    setError('');
    try {
      const path = completed
        ? `/treatments/${selectedTreatment.id}/steps/${stepId}/complete`
        : `/treatments/${selectedTreatment.id}/steps/${stepId}/incomplete`;
      const res = await fetch(getApiUrl(path), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to update treatment step');
      fetchCoreData();
    } catch (e: any) {
      setError(e?.message || 'Failed to update treatment step');
    }
  };

  const saveEhrEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient?.id) return;
    setEhrSaving(true);
    setError('');

    const description = [
      ehrFreeText.trim() ? `Clinical note:\n${ehrFreeText.trim()}` : '',
      ehrChiefComplaint.trim() ? `Chief complaint: ${ehrChiefComplaint.trim()}` : '',
      ehrExam.trim() ? `Exam: ${ehrExam.trim()}` : '',
      ehrDiagnosis.trim() ? `Diagnosis: ${ehrDiagnosis.trim()}` : '',
      ehrPlan.trim() ? `Plan: ${ehrPlan.trim()}` : ''
    ]
      .filter(Boolean)
      .join('\n\n');

    if (!description) {
      setError('Please add free text or structured EHR data before saving');
      setEhrSaving(false);
      return;
    }

    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.MEDICAL_STORIES), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          patientId: patient.id,
          appointmentId: appointmentId || undefined,
          date: ehrDate,
          description
        })
      });
      if (!res.ok) throw new Error('Failed to save EHR note');

      setEhrFreeText('');
      setEhrChiefComplaint('');
      setEhrExam('');
      setEhrDiagnosis('');
      setEhrPlan('');
      fetchCoreData();
      setActiveTab('ehr');
    } catch (err: any) {
      setError(err?.message || 'Failed to save EHR note');
    } finally {
      setEhrSaving(false);
    }
  };

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'ehr', label: 'EHR' },
    { id: 'allergies', label: 'Allergies' },
    { id: 'billing', label: 'Billing' },
    { id: 'photos', label: 'Photos' }
  ];

  const patientName = [patient?.presentedName, `${patient?.firstName || ''} ${patient?.firstLastName || ''}`.trim()]
    .find((value) => typeof value === 'string' && value.trim().length > 0) || 'Patient';

  const defaultPhotoTags = useMemo(() => {
    const tags = [`treatment:${treatmentId}`];
    if (selectedTreatment?.name) {
      tags.push(selectedTreatment.name);
    }
    return tags;
  }, [selectedTreatment?.name, treatmentId]);
  const themedPanelStyle: React.CSSProperties = { ...panelStyle, ...theme.card };
  const themedCardStyle: React.CSSProperties = { ...cardStyle, background: theme.inputBg, border: `1px solid ${theme.border}`, color: theme.text };
  const themedInputStyle: React.CSSProperties = { ...theme.input, padding: 8, borderRadius: 6 };
  const themedSecondaryButton: React.CSSProperties = { ...theme.secondaryButton, padding: '8px 14px' };
  const themedPrimaryButton: React.CSSProperties = { ...theme.button, padding: '8px 14px' };

  const getProfilePictureUrl = (filePath?: string) => {
    if (!filePath) return '';
    const normalizedPath = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
    const base = API_CONFIG.BASE_URL.replace(/\/+$/, '');
    return `${base}/${normalizedPath}`;
  };

  if (loading) {
    return <div style={{ padding: 24, color: theme.text }}>Loading treatment workspace...</div>;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24, color: theme.text }}>
      <div className="responsive-row" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <button
          type="button"
          onClick={onBack}
          style={themedSecondaryButton}
        >
          Back to calendar
        </button>
        {patient?.id && (
          <button
            type="button"
            onClick={() => onOpenPatientProfile(patient.id)}
            style={{ ...themedPrimaryButton, background: '#0ea5e9', borderColor: '#0ea5e9' }}
          >
            Open patient profile
          </button>
        )}
      </div>

      <div style={{ ...themedPanelStyle, marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 24 }}>Treatment workspace</h2>
        <div style={{ color: theme.muted, fontSize: 14 }}>
          {patientName} · Treatment #{treatmentId}
        </div>
        {patient?.hasCriticalAllergy && (
          <div style={{ marginTop: 10, display: 'inline-block', padding: '4px 10px', borderRadius: 999, background: '#7f1d1d', color: '#fecaca', fontSize: 12, fontWeight: 700 }}>
            Critical allergy warning
          </div>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 16, background: '#7f1d1d', border: '1px solid #b91c1c', color: '#fecaca', borderRadius: 8, padding: 10 }}>
          {error}
        </div>
      )}

      <div className="responsive-row" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              background: activeTab === tab.id ? theme.activeBg : theme.surface,
              color: activeTab === tab.id ? theme.activeText : theme.text,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="responsive-grid" style={{ ...themedPanelStyle, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
            <div style={themedCardStyle}>
              <div style={{ fontSize: 12, color: theme.muted, marginBottom: 8 }}>Patient</div>
              <div className="responsive-row" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {patient?.profilePicturePath ? (
                  <img
                    src={getProfilePictureUrl(patient.profilePicturePath)}
                    alt={patientName}
                    style={{ width: 44, height: 44, borderRadius: 999, objectFit: 'cover', border: `1px solid ${theme.border}` }}
                  />
                ) : (
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      color: theme.muted,
                      fontWeight: 700
                    }}
                  >
                    {patientName.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{patientName}</div>
                  <div style={{ fontSize: 12, marginTop: 2 }}>{patient?.phoneNumber || 'No phone'}</div>
                  <div style={{ fontSize: 12 }}>{patient?.email || 'No email'}</div>
                </div>
              </div>
            </div>
            <div style={themedCardStyle}>
              <div style={{ fontSize: 12, color: theme.muted }}>Balance due</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: (patient?.balanceDue || 0) > 0 ? '#ef4444' : '#10b981' }}>
                Bs {(patient?.balanceDue || 0).toFixed(2)}
              </div>
            </div>
            <div style={themedCardStyle}>
              <div style={{ fontSize: 12, color: theme.muted }}>Total paid</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>Bs {paymentTotal.toFixed(2)}</div>
            </div>
          </div>

          {!selectedTreatment ? (
            <div style={themedPanelStyle}>Treatment not found for this patient.</div>
          ) : (
            <div style={themedPanelStyle}>
              <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 20 }}>{selectedTreatment.name}</h3>
                  <div style={{ color: theme.muted, fontSize: 13 }}>Status: {selectedTreatment.status}</div>
                </div>
                <select
                  value={selectedTreatment.status}
                  onChange={(e) => changeTreatmentStatus(e.target.value)}
                  style={{ ...themedInputStyle, padding: '8px 10px' }}
                >
                  <option value="in_progress">in progress</option>
                  <option value="with_debt">with debt</option>
                  <option value="completed">completed</option>
                  <option value="archived">archived</option>
                </select>
              </div>

              <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
                <div style={themedCardStyle}><strong>Cost:</strong> Bs {selectedTreatment.totalCost.toFixed(2)}</div>
                <div style={themedCardStyle}><strong>Paid:</strong> Bs {selectedTreatment.totalPaid.toFixed(2)}</div>
                <div style={{ ...themedCardStyle, color: selectedTreatment.totalDue > 0.005 ? '#ef4444' : theme.text }}><strong>Due:</strong> Bs {selectedTreatment.totalDue.toFixed(2)}</div>
                <div style={themedCardStyle}><strong>Completed:</strong> {selectedTreatment.isCompleted ? 'Yes' : 'No'}</div>
              </div>

              {selectedTreatment.isMultiStep && (
                <div>
                  <h4 style={{ marginTop: 8 }}>Treatment steps</h4>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(selectedTreatment.steps || []).map((step) => (
                      <div className="responsive-row" key={step.id} style={{ ...themedCardStyle, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>#{step.stepNumber} {step.name}</div>
                          <div style={{ fontSize: 12, color: theme.muted }}>{step.description || 'No description'}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateStepCompletion(step.id, !step.completed)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: 'none',
                            background: step.completed ? '#64748b' : '#10b981',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {step.completed ? 'Mark incomplete' : 'Mark complete'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'ehr' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <form onSubmit={saveEhrEntry} style={{ ...themedPanelStyle, display: 'grid', gap: 10 }}>
            <h3 style={{ margin: 0 }}>EHR entry</h3>
            <div className="responsive-grid" style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <BolivianDateInput value={ehrDate} onChange={setEhrDate} style={themedInputStyle} />
              <input value={ehrChiefComplaint} onChange={(e) => setEhrChiefComplaint(e.target.value)} placeholder="Chief complaint" style={themedInputStyle} />
              <input value={ehrExam} onChange={(e) => setEhrExam(e.target.value)} placeholder="Exam" style={themedInputStyle} />
              <input value={ehrDiagnosis} onChange={(e) => setEhrDiagnosis(e.target.value)} placeholder="Diagnosis" style={themedInputStyle} />
              <input value={ehrPlan} onChange={(e) => setEhrPlan(e.target.value)} placeholder="Plan" style={themedInputStyle} />
            </div>
            <textarea
              value={ehrFreeText}
              onChange={(e) => setEhrFreeText(e.target.value)}
              placeholder="Free-text clinical notes"
              rows={4}
              style={themedInputStyle}
            />
            <button
              type="submit"
              disabled={ehrSaving}
              style={{ width: 'fit-content', padding: '8px 14px', borderRadius: 6, border: 'none', background: '#0ea5e9', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
            >
              {ehrSaving ? 'Saving...' : 'Save EHR entry'}
            </button>
          </form>

          <div style={themedPanelStyle}>
            <h3 style={{ marginTop: 0 }}>EHR history</h3>
            {stories.length === 0 ? (
              <div style={{ color: theme.muted }}>No EHR notes yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {stories.map((story) => (
                  <div key={story.id} style={themedCardStyle}>
                    <div style={{ fontSize: 12, color: theme.muted, marginBottom: 6 }}>
                      {formatDate(story.date)}{story.appointmentId ? ` · Appointment ${story.appointmentId}` : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{story.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'allergies' && patient?.id && (
        <div style={themedPanelStyle}>
          <AllergyManagement patientId={patient.id} token={token} />
        </div>
      )}

      {activeTab === 'billing' && patient?.id && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={themedPanelStyle}>
            <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div style={themedCardStyle}>
                <div style={{ fontSize: 12, color: theme.muted }}>Patient balance due</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: (patient.balanceDue || 0) > 0.005 ? '#ef4444' : theme.text }}>Bs {(patient.balanceDue || 0).toFixed(2)}</div>
              </div>
              <div style={themedCardStyle}>
                <div style={{ fontSize: 12, color: theme.muted }}>Selected treatment due</div>
                <div style={{ fontWeight: 700, fontSize: 20, color: (selectedTreatment?.totalDue || 0) > 0.005 ? '#ef4444' : theme.text }}>Bs {(selectedTreatment?.totalDue || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
          <div style={themedPanelStyle}>
            <PaymentList token={token} patientId={patient.id} />
          </div>
        </div>
      )}

      {activeTab === 'photos' && patient?.id && (
        <div style={themedPanelStyle}>
          <PatientPhotoGallery
            patientId={patient.id}
            token={token}
            enableCameraCapture={true}
            defaultUploadTags={defaultPhotoTags}
          />
        </div>
      )}
    </div>
  );
}


